import * as React from 'react';
import {
  SelectorListItem,
  SelectorListSize,
  SelectorListImageSize,
  SelectorListImageShape,
} from './Content';

export type SelectorListSingleProps = {
  /** Renders checkboxes instead of radio buttons and allows users to select multiple items
   * @default false
   */
  multiple?: false;
};

export type SelectorListMultipleProps = {
  /** Renders checkboxes instead of radio buttons and allows users to select multiple items
   * @default false
   */
  multiple: true;
};

export type SelectorListProps = SelectorListCommonProps &
  (SelectorListSingleProps | SelectorListMultipleProps);

export type SelectorListRenderProp = (args: {
  renderList: () => React.ReactNode;
  /** Displays a checkbox which allows to select and deselect all items at once */
  renderToggleAllCheckbox: () => React.ReactNode;
  selectedItems: SelectorListItem[];
}) => React.ReactNode;

export type SelectorListCommonProps = {
  /** Applies a data-hook HTML attribute to be used in the tests */
  dataHook?: string;
  /**
   * Returns data source for the list described in a following structure:
   *
   * ```typescript
   * (searchQuery: string, offset: number, limit: number) =>
   * Promise<{
   *  items: Array<{
   *    id: number | string, // sets the unique item ID (required)
   *    title: node, defines // an item’s title (required)
   *    subtitle?: string, // defines an item’s subtitle
   *    extraText?: string, // contains any text at the end of an item
   *    extraNode?: node, // contains any component at the end of an item
   *    disabled?: boolean, // controls if an item is disabled for selection or not
   *    selected?: boolean, // sets an item as selected
   *    indeterminate?: boolean, // sets an item as indeterminate
   *    image?: node, // contains <Image/> or other component to illustrate an item
   *    subtitleNode?: node, // contains any component below an item’s subtitle
   *    belowNode?: node, // contains any component below the item, to be shown after an item is selected
   *    showBelowNodeOnSelect?: boolean, // allows to show belowNode content when an item is selected
   *  }>,
   *  offset: number, // specifies the item index in the data source to start fetching from<br>
   *  limit: number, // sets a max amount of items to load from the data source<br>
   *  totalCount: number, // sets a max amount of items to load from the data source on user’s search query
   * }>
   * ```
   * */
  dataSource: SelectorListDatasourceFn;
  /** Controls the size of component paddings and list items
   * @default 'medium'
   */
  size?: SelectorListSize;
  /** Controls the size of item images. Note: `portrait` and `cinema` sizes are only compatible with `rectangular` image shape. */
  imageSize?: SelectorListImageSize;
  /**
   * Controls the shape of item images
   *
   * @default 'rectangular'
   */
  imageShape?: SelectorListImageShape;
  /**
   * Use to display a divider between items
   * @default false
   */
  showDivider?: boolean;
  /** Defines placeholder value shown in the search input
   * @default 'Search...'
   */
  searchPlaceholder?: string;
  /**
   * Contains a component which is shown when there are no items to display in the selector list.
   *
   * i.e. empty `{items:[], totalCount: 0}` was returned on the first call to `dataSource`. Render `<EmptyState/>` component in `section` skin for this purpose.
   * */
  emptyState?: React.ReactNode;
  /**
   * Defines a function that gets the current `searchQuery` and returns the component that is shown when no items are found. Render `<EmptyState />` component in `section` skin for this purpose.
   *  */
  renderNoResults?: (searchValue: string) => React.ReactNode;
  /** Sets the number of items to be loaded each time users scroll down to the end of the list
   * @default 50
   */
  itemsPerPage?: number;
  /** Controls whether to display the search input
   * @default true
   */
  withSearch?: boolean;
  /** Sets search debounce in milliseconds */
  searchDebounceMs?: number;
  /** Sets the maximum number of characters allowed in the search input */
  searchMaxLength?: number;
  /** Sets the height of the component in % or px
   * @default '100%'
   */
  height?: string;
  /** Sets the maximum height of the component in % or px
   * @default '100%'
   */
  maxHeight?: string;
  /** Defines callback that triggers on select and return selected item object */
  onSelect?: (item: SelectorListItem) => void;
  /** Sets the number of items to load on initial render or after search. If not defined, it will be equal to `itemsPerPage` value. */
  initialAmountToLoad?: number;
  children?: SelectorListRenderProp;
  /** Contains text or other component in a fixed position at the top of the list */
  subtitle?: React.ReactNode;
  /** Focus the element on mount (standard React input autoFocus). */
  autoFocus?: boolean;
  /** Sets the label for the checkbox which allows to select all items
   * @default 'Select all'
   */
  selectAllText?: string;
  /** Sets the label for the checkbox which allows to deselect all selected items
   * @default 'Deselect all'
   */
  deselectAllText?: string;
};

export class SelectorList extends React.PureComponent<SelectorListProps> {
  reloadInitialItems: () => void;
}

export type SelectorListDatasourceFn = (
  searchQuery: string,
  offset: number,
  limit: number,
) => Promise<{ items: SelectorListItem[]; totalCount: number }>;

export {
  SelectorListItem,
  SelectorListSize,
  SelectorListImageSize,
  SelectorListImageShape,
};
